/**
 * Reads the default export of a CommonJS package the way the browser needs it.
 *
 * Vite 8 bundles with Rolldown, and Rolldown resolves a CommonJS default import
 * under **Node's** interop rules: `import X from "pkg"` becomes
 * `__toESM(require_pkg(), 1)`, and that `1` says "the whole `module.exports` is
 * the default export". Node really does behave that way, because Node ignores
 * the `__esModule` marker. Bundlers historically do not: webpack and Rollup
 * honour `__esModule` and hand back `module.exports.default`.
 *
 * For a package built by Rollup or TypeScript, which writes
 * `exports.__esModule = true; exports.default = Component`, the two rules
 * disagree, and the disagreement is silent. `X` is the exports **object**
 * rather than the component, TypeScript still types it as the component
 * because it compiles under `esModuleInterop`, and the first render throws
 * React error #130, "element type is invalid ... got: object". React unmounts
 * the tree, so the whole page goes blank rather than that one component
 * failing. That is what blanked /company: `react-fast-marquee` is CommonJS.
 *
 * Vite's dev server does not show it. It pre-bundles dependencies with esbuild
 * and serves real ESM, so the default is already the component and the page
 * works locally right up until it is built.
 *
 * This normalises both shapes. Under a correct bundler the argument is already
 * the component and is returned untouched: a React component, including the
 * object `forwardRef` returns, never carries `__esModule`.
 */
export function interopDefault<T>(imported: T): T {
    const module = imported as { __esModule?: boolean; default?: T } | null;

    if (module && module.__esModule === true && module.default !== undefined) {
        return module.default;
    }
    return imported;
}
